/**
 * Turn a raw backend CRM validation/save error into a concise, human message
 * for inline surfaces (the table cell's error footer).
 *
 * Backend errors are written for API consumers and logs, not end users:
 *
 *   "'Email' element at index 2: Invalid email format: dsfsdf@sdfsdfsdf.
 *    The part after the @-sign is not valid. It should have a period."
 *
 * That leaks positional indexing ("element at index 2"), the raw invalid
 * value, and parser internals ("the part after the @-sign"). The field being
 * edited is already obvious from the column, so we strip the scaffolding and
 * map the common validation failures to a plain sentence.
 *
 * This is a presentation-layer cleanup, not validation: the backend remains
 * the source of truth for what's rejected. Anything we don't recognise falls
 * through to the de-scaffolded raw text so no error is ever swallowed.
 */

// Leading "'Field' element at index N:" / "Field at index N:" scaffolding the
// backend prepends for array (multi-value) elements. The column already names
// the field, and the row is visible, so the prefix is pure noise inline.
const ELEMENT_INDEX_PREFIX = /^'?[\w .]+?'?\s+(?:element\s+)?at index \d+:\s*/i;

// Ordered pattern → friendly message. First match wins.
const FRIENDLY_PATTERNS: { test: RegExp; message: string }[] = [
  {
    test: /invalid email|not a valid email|email format/i,
    message:
      "That email address isn't valid. Check the format (name@example.com) and try again.",
  },
  {
    test: /invalid phone|not.*valid.*phone|phone number.*valid/i,
    message:
      "That phone number isn't valid for the selected country. Check it and try again.",
  },
  {
    test: /invalid url|not a valid url|url format/i,
    message: "That web address isn't valid. Check the format and try again.",
  },
  {
    test: /invalid domain|not a valid domain|domain format/i,
    message: "That domain isn't valid. Check the format and try again.",
  },
  {
    test: /unique|already exists|duplicate/i,
    message: 'That value is already in use. Enter a different one.',
  },
  {
    test: /permission denied|not allowed|forbidden/i,
    message: "You don't have permission to make this change.",
  },
  {
    test: /required|cannot be (?:empty|blank|null)|must not be empty/i,
    message: 'This field is required.',
  },
];

/**
 * Map a raw backend error to a friendly, allowlisted sentence — or `null` when
 * no pattern matches. Callers that must NEVER surface raw backend text (e.g.
 * the note description editor, where a save error is more likely to be an
 * internal/technical message than a field-format complaint) fall back to a
 * generic message on `null` instead of showing the de-scaffolded raw text.
 *
 * @param raw the caught error's message (already an Error.message string)
 * @returns an allowlisted friendly sentence, or `null` if unrecognised
 */
export function matchFriendlySaveError(raw: string): string | null {
  const stripped = raw.replace(ELEMENT_INDEX_PREFIX, '').trim();

  for (const { test, message } of FRIENDLY_PATTERNS) {
    // Match against the de-scaffolded text only. Testing `raw` as well would
    // re-examine the stripped "'Field' at index N:" prefix, so a field *name*
    // containing a keyword (e.g. a "unique_code" column) could trigger a false
    // mapping the actual message doesn't warrant.
    if (test.test(stripped)) {
      return message;
    }
  }

  return null;
}

/**
 * @param raw the caught error's message (already an Error.message string)
 * @returns a concise user-facing sentence
 */
export function humanizeSaveError(raw: string): string {
  const mapped = matchFriendlySaveError(raw);
  if (mapped !== null) {
    return mapped;
  }

  // Unrecognised: return the de-scaffolded text so nothing is hidden, capped
  // so an unexpectedly long backend message can't blow out the footer.
  const cleaned =
    raw.replace(ELEMENT_INDEX_PREFIX, '').trim() ||
    'Could not save. Try again.';
  return cleaned.length > 200 ? `${cleaned.slice(0, 197)}…` : cleaned;
}
